import Box from '@amzn/awsui-components-core/box';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import Icon from '@amzn/awsui-components-core/icon';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Token from '@amzn/awsui-components-core/token';
import React, { useCallback, useMemo, useState } from 'react';
import PnlMemberSubsetModal from 'src/components/star-grid/components/PnlMemberSubsetModal';
import styles from './styles/RowsDimensionPicker.module.scss';

/** One draggable dimension chip: the physical fact column, its label, and (optionally) the
 * rollup hierarchy backing its value tree. A fact column can back several hierarchies (line
 * item → P&L Line Item and Channel), so identity is the hierarchyId when present, else field. */
export interface DimensionItem {
  readonly field: string;
  readonly label: string;
  readonly hierarchyId?: string;
}

interface RowsDimensionPickerProps {
  readonly allDimensions: readonly DimensionItem[];
  readonly selectedDimensions: readonly DimensionItem[];
  readonly onChange: (selected: DimensionItem[]) => void;
  readonly isReadOnly?: boolean;
  readonly selectedLabel?: string;
  readonly selectedDescription?: string;
  readonly emptySelectedText?: string;
  readonly availableDescription?: string;
  /** Selected leaf values per chip, keyed by the chip's identity (hierarchyId, else field). */
  readonly values?: Readonly<Record<string, readonly string[]>>;
  /** Fires when a selected chip's value selection changes (keyed by chip identity). */
  readonly onChangeValue?: (id: string, values: string[]) => void;
  /**
   * Fires when a chip's member subset is confirmed in the subset editor (keyed by chip identity).
   * Separate from ``onChangeValue``: the caret picked LEAF SCOPE (which dollars are in play), the ☰
   * editor picks MEMBERS (which rows the report shows).
   */
  readonly onChangeMembers?: (id: string, members: string[]) => void;
  readonly testIdPrefix?: string;
}

/**
 * Two-pane drag-and-drop dimension picker (xPT PnlRowsDimensionPicker shape): drag chips from
 * "Available" into the selected zone to add, reorder within, drag back to remove. When a chip
 * carries a ``hierarchyId`` and ``onChangeValue`` is provided, it shows an inline value picker
 * (PnlDimensionValueSelect) so the user chooses which leaf values to include — the rollup values
 * appear right on the token, as in xPT.
 */
/**
 * The chip's selection summary. `Select` is the prompt for an untouched chip: nothing is chosen yet,
 * which the API reads as unrestricted. Deliberately not `All`, which would claim every member had
 * been picked.
 */
const memberLabel = (counts?: { picked: number; total: number }): string => {
  if (!counts || counts.picked === 0) return 'Select';
  if (counts.total > 0 && counts.picked >= counts.total) return 'All';
  return `${counts.picked} of ${counts.total}`;
};

const RowsDimensionPicker: React.FC<RowsDimensionPickerProps> = ({
  allDimensions,
  selectedDimensions,
  onChange,
  isReadOnly = false,
  selectedLabel = 'Rows',
  selectedDescription = 'Report will show these row dimensions in order',
  emptySelectedText = 'Drag dimensions here to add as rows.',
  availableDescription = 'Drag these to the left to add as rows in your report',
  values,
  onChangeValue,
  onChangeMembers,
  testIdPrefix
}) => {
  const idOf = useCallback((d: DimensionItem): string => d.hierarchyId ?? d.field, []);
  // Which chip's subset editor is open (chip identity), or null. One modal at a time.
  const [subsetChipId, setSubsetChipId] = useState<string | null>(null);
  // What each chip's editor last confirmed, for the `x of y` label. Display only — persistence
  // travels through onChangeMembers.
  const [memberCounts, setMemberCounts] = useState<Record<string, { picked: number; total: number; keys: string[] }>>({});

  const availableDimensions = useMemo(
    () => allDimensions.filter((d) => !selectedDimensions.some((s) => idOf(s) === idOf(d))),
    [allDimensions, selectedDimensions, idOf]
  );

  const [dragId, setDragId] = useState<string | null>(null);
  const [dragSource, setDragSource] = useState<'selected' | 'available' | null>(null);
  const [overSelected, setOverSelected] = useState(false);
  const [overAvailable, setOverAvailable] = useState(false);

  const resetDrag = useCallback(() => {
    setDragId(null);
    setDragSource(null);
    setOverSelected(false);
    setOverAvailable(false);
  }, []);

  const handleDropOnSelected = useCallback(
    (e: React.DragEvent, insertIndex?: number) => {
      e.preventDefault();
      if (!dragId) return;
      const item = allDimensions.find((d) => idOf(d) === dragId);
      if (!item) {
        resetDrag();
        return;
      }
      const without = selectedDimensions.filter((d) => idOf(d) !== dragId);
      const at = insertIndex ?? without.length;
      onChange([...without.slice(0, at), item, ...without.slice(at)]);
      resetDrag();
    },
    [dragId, allDimensions, selectedDimensions, onChange, resetDrag, idOf]
  );

  const handleDropOnAvailable = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!dragId || dragSource !== 'selected') {
        resetDrag();
        return;
      }
      onChange(selectedDimensions.filter((d) => idOf(d) !== dragId));
      resetDrag();
    },
    [dragId, dragSource, selectedDimensions, onChange, resetDrag, idOf]
  );

  const selectedZoneTid = testIdPrefix ? `${testIdPrefix}-selected` : undefined;
  const availableZoneTid = testIdPrefix ? `${testIdPrefix}-available` : undefined;
  const selectedChipTid = testIdPrefix ? `${testIdPrefix}-selected-chip` : undefined;
  const availableChipTid = testIdPrefix ? `${testIdPrefix}-available-chip` : undefined;

  if (isReadOnly) {
    return (
      <SpaceBetween direction="horizontal" size="xs">
        {selectedDimensions.map((d) => (
          <Token key={idOf(d)} label={d.label} />
        ))}
      </SpaceBetween>
    );
  }

  return (
    <ColumnLayout columns={2}>
      <SpaceBetween size="xxs">
        <Box fontWeight="bold" fontSize="body-s">
          {selectedLabel} ({selectedDimensions.length}/{allDimensions.length})
        </Box>
        <Box color="text-body-secondary" fontSize="body-s">
          {selectedDescription}
        </Box>
        <div
          className={`${styles.dropZone} ${overSelected ? styles.dropZoneActive : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOverSelected(true);
          }}
          onDragLeave={() => setOverSelected(false)}
          onDrop={(e) => handleDropOnSelected(e)}
          data-testid={selectedZoneTid}
        >
          {selectedDimensions.length === 0 && (
            <Box color="text-body-secondary" fontSize="body-s">
              {emptySelectedText}
            </Box>
          )}
          {selectedDimensions.map((d, idx) => (
            <span
              key={idOf(d)}
              className={styles.chipSelected}
              draggable
              onDragStart={() => {
                setDragId(idOf(d));
                setDragSource('selected');
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.stopPropagation();
                handleDropOnSelected(e, idx);
              }}
              onDragEnd={resetDrag}
              style={{ opacity: dragId === idOf(d) ? 0.4 : 1 }}
              data-testid={selectedChipTid}
              data-field={d.field}
            >
              <span className={styles.chipLabel}>{d.label}</span>
              {d.hierarchyId && (
                <span className={styles.chipValue} draggable={false} onDragStart={(e) => e.preventDefault()}>
                  {/*
                   * The inline leaf-value dropdown is parked, not deleted: the ☰ subset editor now
                   * covers member choice, and having both on one chip meant two overlapping
                   * selections to explain and to persist. Restore this block (and pass `values` /
                   * `onChangeValue` again) if leaf-level scoping needs to sit beside the editor.
                   *
                   * {onChangeValue && (
                   *   <PnlDimensionValueSelect
                   *     hierarchyId={d.hierarchyId}
                   *     ariaLabel={`${d.label} values`}
                   *     selectedValues={values?.[idOf(d)]}
                   *     onChange={(v) => onChangeValue(idOf(d), v)}
                   *     testId={selectedChipTid ? `${selectedChipTid}-value` : undefined}
                   *   />
                   * )}
                   */}
                  <span className={styles.chipCount} data-testid={selectedChipTid ? `${selectedChipTid}-count` : undefined}>
                    {memberLabel(memberCounts[idOf(d)])}
                  </span>
                  <button
                    type="button"
                    className={styles.chipMenuButton}
                    onClick={() => setSubsetChipId(idOf(d))}
                    aria-label={`Select ${d.label} members`}
                    aria-haspopup="dialog"
                    data-testid={selectedChipTid ? `${selectedChipTid}-subset` : undefined}
                  >
                    <Icon name="filter" size="small" />
                  </button>
                </span>
              )}
            </span>
          ))}
        </div>
      </SpaceBetween>

      <SpaceBetween size="xxs">
        <Box fontWeight="bold" fontSize="body-s">
          Available Dimensions
        </Box>
        <Box color="text-body-secondary" fontSize="body-s">
          {availableDescription}
        </Box>
        <div
          className={`${styles.dropZone} ${overAvailable ? styles.dropZoneActive : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOverAvailable(true);
          }}
          onDragLeave={() => setOverAvailable(false)}
          onDrop={handleDropOnAvailable}
          data-testid={availableZoneTid}
        >
          {availableDimensions.length === 0 && (
            <Box color="text-body-secondary" fontSize="body-s">
              All dimensions are in use.
            </Box>
          )}
          {availableDimensions.map((d) => (
            <span
              key={idOf(d)}
              className={styles.chipAvailable}
              draggable
              onDragStart={() => {
                setDragId(idOf(d));
                setDragSource('available');
              }}
              onDragEnd={resetDrag}
              style={{ opacity: dragId === idOf(d) ? 0.4 : 1 }}
              data-testid={availableChipTid}
              data-field={d.field}
            >
              <Token label={d.label} />
            </span>
          ))}
        </div>
      </SpaceBetween>

      {selectedDimensions
        .filter((d) => d.hierarchyId && idOf(d) === subsetChipId)
        .map((d) => (
          <PnlMemberSubsetModal
            key={idOf(d)}
            hierarchyId={d.hierarchyId as string}
            dimensionLabel={d.label}
            visible
            onDismiss={() => setSubsetChipId(null)}
            // Always set: the chip's own label depends on it, so gating this on an optional
            // persistence callback left OK doing nothing at all whenever that callback was absent.
            // The position KEYS come back too, so reopening the editor shows the same ticks. Member
            // ids alone could not: one id can sit at several positions.
            initialChecked={memberCounts[idOf(d)]?.keys}
            onConfirm={(members, total, checkedKeys) => {
              setMemberCounts((current) => ({ ...current, [idOf(d)]: { picked: members.length, total, keys: checkedKeys } }));
              onChangeMembers?.(idOf(d), members);
            }}
            testId={selectedChipTid ? `${selectedChipTid}-subset-modal` : undefined}
          />
        ))}
    </ColumnLayout>
  );
};

export default RowsDimensionPicker;
